;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import Carousel from './Carousel';

import styles from './PersonalizedRecipesCarousel.module.scss';

const PersonalizedRecipesCarousel = ({
    className,
    cardMap,
    cards,
    navigationTheme,
    isLoading,
    onIsBeginning,
    ...rest
}) => {
    const carousel1Ref = useRef(null);
    const carousel2Ref = useRef(null);

    // Track which carousel is currently active (front)
    const [activeCarousel, setActiveCarousel] = useState(1);

    // Store previous cards during transition
    const [previousCards, setPreviousCards] = useState(cards);
    const [transitionPhase, setTransitionPhase] = useState('idle'); // 'idle', 'loading', 'preparing', 'ready'

    // Update previous cards and swap carousel when loading starts
    useEffect(() => {
        if (isLoading && transitionPhase === 'idle') {
            setPreviousCards(cards);
            setTransitionPhase('loading');
            // Swap the active carousel immediately so new content can load in the now-active one
            setActiveCarousel(prev => (prev === 1 ? 2 : 1));
        }
    }, [isLoading, transitionPhase, cards]);

    // Handle card updates - prepare new carousel
    useEffect(() => {
        if (transitionPhase === 'loading' && cards !== previousCards) {
            setTransitionPhase('preparing');
        }
    }, [transitionPhase, cards, previousCards]);

    // Handle the reset after the new carousel has new content
    useEffect(() => {
        let ref;
        let timeout;

        if (transitionPhase === 'preparing') {
            const activeCarouselRef =
                activeCarousel === 1 ? carousel1Ref : carousel2Ref;
            activeCarouselRef.current?.reset();

            ref = requestAnimationFrame(() => {
                timeout = setTimeout(() => {
                    setTransitionPhase('ready');
                }, 200);
            });
        }
        return () => {
            if (ref) cancelAnimationFrame(ref);
            if (timeout) clearTimeout(timeout);
        };
    }, [transitionPhase, activeCarousel]);

    // Wait for carousel reset to finish, then do final animation
    useEffect(() => {
        let timeout;

        if (transitionPhase === 'ready') {
            timeout = setTimeout(() => {
                setTransitionPhase('idle');
            }, 600);
        }
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [transitionPhase]);

    const cleanCards = cards.filter(card => {
        const isRenderable =
            (cardMap[card?.__typename || 'Default'] || cardMap['Default']) !==
            null;

        if (!isRenderable && card?.__typename) {
            console.warn(
                `Card is not renderable __typename: ${card?.__typename}`
            );
        }

        return isRenderable;
    });

    const cleanPreviousCards = previousCards.filter(card => {
        const isRenderable =
            (cardMap[card?.__typename || 'Default'] || cardMap['Default']) !==
            null;

        return isRenderable;
    });

    const renderCards = cardsToRender => {
        return cardsToRender.map((card, idx) => {
            const Card =
                cardMap[card.__typename || 'Default'] || cardMap['Default'];

            if (Card == null) {
                console.warn(`Card Missing or Drafted`);
                return null;
            }

            return (
                <div key={`${card.slug || idx}`} className={styles.item}>
                    <div className={styles.inner}>
                        <div className={styles.card}>
                            <Card
                                {...card}
                                id={card.sys?.id}
                                index={idx}
                                {...rest}
                            />
                        </div>
                        <div className={styles.skeletonCard} aria-hidden="true">
                            <div className={styles.skeletonImage} />
                            <div className={styles.skeletonParagraph} />
                            <div className={styles.skeletonParagraph} />
                        </div>
                    </div>
                </div>
            );
        });
    };

    // Determine what each carousel should render based on transition phase
    const getCarousel1Content = () => {
        if (activeCarousel === 1) {
            return renderCards(cleanCards);
        } else {
            return renderCards(cleanPreviousCards);
        }
    };

    const getCarousel2Content = () => {
        if (activeCarousel === 2) {
            return renderCards(cleanCards);
        } else {
            return renderCards(cleanPreviousCards);
        }
    };

    return (
        <div
            className={cx(styles.root, className, {
                [styles.isTransition]: transitionPhase !== 'idle',
                [styles.isReady]: transitionPhase === 'ready',
            })}
        >
            <div className={styles.carouselSwapContainer}>
                <div
                    className={cx(
                        styles.carouselContainer,
                        styles.carousel1Container,
                        {
                            [styles.isActive]: activeCarousel === 1,
                        }
                    )}
                >
                    <Carousel
                        ref={carousel1Ref}
                        className={styles.carousel}
                        navigationTheme={navigationTheme}
                        slideClassName={styles.slide}
                        autoScroll={false}
                        inlineIntro
                        hideScrollBars
                        onIsBeginning={
                            activeCarousel === 1 ? onIsBeginning : null
                        }
                        disableNavigation={
                            transitionPhase !== 'idle' || activeCarousel === 2
                        }
                    >
                        {getCarousel1Content()}
                    </Carousel>
                </div>
                <div
                    className={cx(
                        styles.carouselContainer,
                        styles.carousel2Container,
                        {
                            [styles.isActive]: activeCarousel === 2,
                        }
                    )}
                >
                    <Carousel
                        ref={carousel2Ref}
                        className={styles.carousel}
                        navigationTheme={navigationTheme}
                        slideClassName={styles.slide}
                        autoScroll={false}
                        inlineIntro
                        hideScrollBars
                        onIsBeginning={
                            activeCarousel === 2 ? onIsBeginning : null
                        }
                        disableNavigation={
                            transitionPhase !== 'idle' || activeCarousel === 1
                        }
                    >
                        {getCarousel2Content()}
                    </Carousel>
                </div>
            </div>
        </div>
    );
};

PersonalizedRecipesCarousel.propTypes = {
    className: PropTypes.string,
    cards: PropTypes.array.isRequired,
    cardMap: PropTypes.object,
    navigationTheme: PropTypes.oneOf(['blue', 'cream', 'orange']),
    isLoading: PropTypes.bool,
};

export default PersonalizedRecipesCarousel;
